<template>
  <div
    v-if="tags.length"
    class="sidepane-tags sf-level-item sf-btn-bar sf-btn-bar--no-clip"
  >
    <div class="sf-btn-bar-item sidepane-tags__select">
      <SfDropdown
        dropdown-id="tagfilter"
        :is-open="isScopedDropdownOpen"
        variant="seamless"
        @dropdown-clicked="handleToggleDropdown"
      >
        <template #trigger>
          <div class="sidepane-tags__trigger-container">
            <div>{{ I18n.t('account_entry.filter').toUpperCase() }}</div>
            <div class="icon-tag sf-pad-r-1 sf-pad-l-1" />
            <template v-if="currentTag">
              <span
                class="sidepane-tags__name sidepane-tags__trigger-text"
                :class="'sidepane-tags__name--pinned'"
              >
                {{ currentTag.name }}
              </span>
              <span class="sf-label sidepane-tags__label">
                {{ currentTag.items.length }}
              </span>
            </template>
            <div v-else class="sidepane-tags__trigger-text">
              {{
                I18n.t('companies_overview.filters.comparison_modes.show_all')
              }}
            </div>
          </div>
        </template>

        <template #menu>
          <div
            class="sf-tab-item"
            :class="showAllActiveClass"
            @click.stop="clearTagSelection"
          >
            {{ I18n.t('companies_overview.filters.comparison_modes.show_all') }}
          </div>
          <div
            v-for="tag in tags"
            :key="tag.id"
            class="sf-tab-item"
            :class="activeClass(tag)"
            @click.stop="tagSelected(tag)"
          >
            <span>
              {{ tag.name }}
            </span>
            <span class="sf-label sidepane-tags__label">
              {{ tag.items.length }}
            </span>
          </div>
        </template>
      </SfDropdown>
    </div>
    <a
      href="#"
      class="
        sf-btn sf-btn-icon
        sidepane-tags__navigator sidepane-tags__button--previous
      "
      :class="btnPrevClass"
      @click.stop="prevItem"
    >
      <i
        class="icon-chevron-left"
        data-testid="sidepane-tags-previous-button"
      />
    </a>
    <a
      href="#"
      class="
        sf-btn sf-btn-icon
        sidepane-tags__navigator sidepane-tags__button--next
      "
      :class="btnNextClass"
      @click.stop="nextItem"
    >
      <i class="icon-chevron-right" data-testid="sidepane-tags-next-button" />
    </a>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia'

import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import { dropdowns } from '@app/shared/vue/store/modules/dropdown/constants'

import { useWorkflowReviewFocus } from '../store/pinia/workflow-review-focus'
import { useWorfklowReviewItems } from '../store/pinia/workflow-review-items'
import { useDropdown } from '@app/shared/pinia/dropdown'

const WorkflowReviewSidepaneTagsFilter = {
  components: {
    SfDropdown,
  },
  props: {
    context: {
      type: Object,
      default: null,
    },
  },
  data: () => {
    return { currentTagItemIndex: 0 }
  },
  computed: {
    ...mapState({
      tags: (state) => state.remarkTags.workflowReviewTags,
      workflowReviewActiveTagId: (state) =>
        state.remarkTags.workflowReviewActiveTagId,
    }),
    ...mapPiniaState(useDropdown, ['currentDropdownId']),
    ...mapPiniaState(useWorfklowReviewItems, [
      'currentReviewItem',
      // ids of all RTs visible on the Workflow Review pane
      'reviewItems',
    ]),
    currentTag() {
      return this.tags.find((x) => x.id === this.workflowReviewActiveTagId)
    },
    // ids of RTs that have remarks with the selected Tag
    currentTagItems() {
      return this.currentTag ? this.currentTag.items : []
    },
    // disable arrows if no Tag selected or only 1 RT for the selected Tag
    btnNextClass() {
      return {
        'sf-btn-disabled':
          (this.currentTag && this.isLastItemSelected) || !this.currentTag,
        'sf-btn-bar-item': true,
      }
    },
    btnPrevClass() {
      return {
        'sf-btn-disabled':
          (this.currentTag && this.isFirstItemSelected) || !this.currentTag,
        'sf-btn-bar-item': true,
      }
    },
    contextItemIndex() {
      return this.currentTagItems.findIndex(
        (i) =>
          i.id === this.currentReviewItem?.contextId &&
          i.type === this.currentReviewItem?.contextType
      )
    },
    isFirstItemSelected() {
      return this.contextItemIndex === 0
    },
    isLastItemSelected() {
      return this.contextItemIndex === this.currentTagItems.length - 1
    },
    isScopedDropdownOpen() {
      return this.currentDropdownId === dropdowns.REVIEW_COMMS_FILTER
    },
    showAllActiveClass() {
      return this.currentTag ? 'sf-pointer' : 'sf-dim-el'
    },
  },
  methods: {
    ...mapActions(['setWorkflowReviewActiveTag']),
    ...mapPiniaActions(useDropdown, ['toggleDropdown', 'closeDropdown']),
    ...mapPiniaActions(useWorkflowReviewFocus, ['triggerNextReviewItemFocus']),
    activeClass(tag) {
      return tag.id === this.workflowReviewActiveTagId
        ? 'sf-dim-el'
        : 'sf-pointer'
    },
    clearTagSelection() {
      this.closeDropdown(dropdowns.REVIEW_COMMS_FILTER)
      this.setWorkflowReviewActiveTag(null)
    },
    handleToggleDropdown() {
      this.toggleDropdown(dropdowns.REVIEW_COMMS_FILTER)
    },
    tagSelected(tag) {
      this.closeDropdown(dropdowns.REVIEW_COMMS_FILTER)
      this.setWorkflowReviewActiveTag(tag)

      if (!this.currentTag) return

      // set focus on the firm RT with the tag if an RT with the tag is not currently in focus
      this.currentTagItemIndex =
        this.contextItemIndex === -1 ? 0 : this.contextItemIndex
    },
    nextItem() {
      if (this.currentTagItemIndex < this.currentTagItems.length - 1) {
        this.currentTagItemIndex++
      } else {
        this.currentTagItemIndex = 0
      }

      this.focusOnItem()
    },
    prevItem() {
      if (this.currentTagItemIndex <= 0) {
        this.currentTagItemIndex = this.currentTagItems.length - 1
      } else {
        this.currentTagItemIndex--
      }

      this.focusOnItem()
    },
    focusOnItem() {
      const item = this.currentTagItems[this.currentTagItemIndex]
      const index = this.getItemIndexById(item)

      this.triggerNextReviewItemFocus(index)
    },
    getItemIndexById(item) {
      return this.reviewItems.findIndex(
        (i) => i.contextId === item.id && i.contextType === item.type
      )
    },
  },
}

export default WorkflowReviewSidepaneTagsFilter
</script>

<style scoped>
.sidepane-tags {
  display: flex;
}

.sidepane-tags__name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  display: inline-block;
}

.sidepane-tags__name--pinned {
  max-width: 80px;
}

.sidepane-tags__name--unpinned {
  max-width: 75px;
}

.remark-tags-list {
  float: right;
}

.sidepane-tags__label {
  padding: 1px 8px;
  margin-left: 5px;
  margin-right: 0;
  border-width: 0;
}

.sidepane-tags__button--previous {
  margin-right: 0;
  border-radius: 0;
}

.sidepane-tags__button--next {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.sidepane-tags__select {
  flex: 1 1 auto;
}

.sidepane-tags__navigator {
  flex: 0 1 36px;
}

.sidepane-tags__trigger-container {
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.sidepane-tags__trigger-text {
  max-width: 80px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
